import { useStoreState } from 'easy-peasy'
import { useCallback, useEffect, useState } from 'react'
import {
  useOrganizationControllerCreateStripeConnectAccount,
  useOrganizationControllerCreateStripeConnectAccountLink
} from '~/clinician-api'

export type StripeConnectPageState =
  | 'NOT_CONNECTED' // no Stripe account yet
  | 'CONNECTED_PENDING' // account exists, onboarding not finished
  | 'CONNECTED_COMPLETE' // account exists, onboarding complete

interface StripeAccountLink {
  url: string
  type: 'account_onboarding' | 'express_dashboard' | string
}

export const useStripeConnectStatus = () => {
  // @ts-expect-error – auth model typing in easy-peasy store is loose in legacy code
  const { user } = useStoreState(state => state.auth)
  const stripeConnectAccountId: string | undefined =
    user?.clinic.organization.stripe_connect_account_id
  const organizationId: string | undefined = user?.clinic.organization_id

  const {
    mutateAsync: createAccountAsync,
    isLoading: isCreatingAccount,
    error: createAccountError
  } = useOrganizationControllerCreateStripeConnectAccount()

  const {
    mutateAsync: createLinkAsync,
    isLoading: isCreatingLink,
    error: createLinkError
  } = useOrganizationControllerCreateStripeConnectAccountLink()

  const [state, setState] = useState<StripeConnectPageState>('NOT_CONNECTED')
  const [cachedLink, setCachedLink] = useState<StripeAccountLink | null>(null)
  const [busy, setBusy] = useState<boolean>(false)
  const [internalError, setInternalError] = useState<unknown>(null)

  const decidePageState = (
    link: StripeAccountLink | null
  ): StripeConnectPageState => {
    if (!stripeConnectAccountId) return 'NOT_CONNECTED'
    if (link && link.type === 'express_dashboard') return 'CONNECTED_COMPLETE'
    return 'CONNECTED_PENDING'
  }

  // On mount (+ when account id changes) fetch account-link to know status
  useEffect(() => {
    const fetchStatus = async () => {
      if (!stripeConnectAccountId || !organizationId) {
        setState('NOT_CONNECTED')
        return
      }

      setBusy(true)
      try {
        const res = await createLinkAsync({
          organizationId,
          data: {
            returnUrl: window.location.origin + '/settings/bank-and-payments',
            refreshUrl: window.location.origin + '/settings/bank-and-payments'
          }
        })
        const link = res as StripeAccountLink
        setCachedLink(link)
        setState(decidePageState(link))
      } catch (err) {
        console.error(err)
        setInternalError(err)
      } finally {
        setBusy(false)
      }
    }

    fetchStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeConnectAccountId, organizationId])

  // Create Stripe account (for NOT_CONNECTED state)
  const initiateAccountCreation = useCallback(async () => {
    if (!organizationId) return
    await createAccountAsync({ organizationId })
    // The backend should immediately attach the new stripe_connect_account_id
    // to the organization and update our auth store; when that happens the
    // useEffect above will run again and pull the fresh status.
  }, [createAccountAsync, organizationId])

  // Fetch/cached appropriate link for button click (onboarding or dashboard)
  const getFreshLink = useCallback(
    async (purpose: 'onboarding' | 'dashboard'): Promise<StripeAccountLink> => {
      // Use cached link if it matches the purpose
      if (cachedLink) {
        if (purpose === 'dashboard' && cachedLink.type === 'express_dashboard')
          return cachedLink
        if (
          purpose === 'onboarding' &&
          cachedLink.type === 'account_onboarding'
        )
          return cachedLink
      }

      if (!organizationId) throw new Error('Organization ID missing')

      const newLink = (await createLinkAsync({
        organizationId,
        data: {
          returnUrl: window.location.origin + '/settings/bank-and-payments',
          refreshUrl: window.location.origin + '/settings/bank-and-payments'
        }
      })) as StripeAccountLink

      setCachedLink(newLink)
      return newLink
    },
    [cachedLink, createLinkAsync, organizationId]
  )

  const combinedError = internalError || createAccountError || createLinkError
  const loading = busy || isCreatingAccount || isCreatingLink

  return {
    state,
    loading,
    error: combinedError,
    initiateAccountCreation,
    getFreshLink
  }
}
